/**
Fake backend for demo mode. Replays API responses from a captured snapshot
instead of hitting the real server. Missing requests return an empty response.
snapshotCapture.ts and this file must generate identical keys. Params are
sorted to ensure requests with different param orders resolve to the same key.
*/

import { trimToRecentMonths } from './demoDataWindow';

export interface SnapshotEntry {
  key: string;
  method: string;
  path: string;
  params?: Record<string, unknown>;
  status: number;
  data: unknown;
}

export interface SnapshotBundle {
  version: 1;
  captured_at?: number;
  label?: string;
  entries: Record<string, SnapshotEntry>;
  /** Query params stripped before keying, e.g. cache-busters. Matched by path prefix. */
  ignore_params?: Record<string, string[]>;
}

/**
 * Params ignored on every route regardless of bundle config.
 *
 * user_id is dropped because capture runs as a real account while playback runs as the
 * synthetic persona, so keeping it would miss every user-scoped route. A demo has one
 * user, so collapsing the variants is what we want.
 */
const GLOBAL_IGNORE_PARAMS = ['t', '_', 'ts', 'cacheBust', 'user_id', 'impersonate_user_id'];

export interface SnapshotMiss {
  method: string;
  path: string;
  params?: unknown;
  at: number;
}

let bundle: SnapshotBundle | null = null;
const misses: SnapshotMiss[] = [];

// Data sources the demo shouldn't name: ChemOrbis and ACC.
// Rules run top to bottom on each bit of text. Longest phrases first, so
// "per ACC data" goes as a whole before the plain "ACC" rule can see it.
// The ACC rules are case-sensitive on purpose, or filenames like
// v_acc_pe.json would get mangled too.
const SOURCE_MENTIONS_TO_REMOVE: Array<[RegExp, string]> = [
  [/,?\s*according to (?:the )?ChemOrbis(?: Price Index)?/gi, ''],
  [/,?\s*(?:as\s+reported\s+)?(?:[Pp]er|[Aa]ccording\s+to)\s+(?:the\s+)?(?:[Ff]inal|[Pp]reliminary)?\s*ACC(?:'s)?\s+data\b/g, ''],
  [/\bACC's\s+/g, ''],
  [/\bACC\s+(?=data\b)/g, ''],
  [/\s*\bACC\b\s*/g, ' '],
];

// Same thing, but for a mention that starts a sentence. Drops it and capitalizes
// the next word, so "ACC's final March data" reads "Final March data".
const SOURCE_MENTIONS_AT_SENTENCE_START = [
  /(^|[>.!?]\s*|&nbsp;\s*)(?:[Aa]ccording\s+to|[Pp]er)\s+(?:the\s+)?(?:final|preliminary)?\s*ACC(?:'s)?\s+data\s*,?\s*(\p{L})/gu,
  /(^|[>.!?]\s*|&nbsp;\s*)ACC's\s+(\p{L})/gu,
];

// Cleans up what removing a phrase leaves behind: double spaces, a space
// before a comma or period, a comma at the very start.
const LEFTOVER_CLEANUP: Array<[RegExp, string]> = [
  [/[ \t]{2,}/g, ' '],
  [/\s+([.,;:!?])/g, '$1'],
  [/^(\s*)[,;]\s*/, '$1'],
];

export function removeSourceMentions(text: string): string {
  let cleaned = text;
  for (const rule of SOURCE_MENTIONS_AT_SENTENCE_START) {
    cleaned = cleaned.replace(
      rule,
      (_match, before: string, letter: string) => before + letter.toUpperCase(),
    );
  }
  for (const [pattern, replacement] of SOURCE_MENTIONS_TO_REMOVE) {
    cleaned = cleaned.replace(pattern, replacement);
  }
  for (const [pattern, replacement] of LEFTOVER_CLEANUP) {
    cleaned = cleaned.replace(pattern, replacement);
  }
  return cleaned;
}

// Walks a response and cleans every piece of text in it. Field names are left alone.
function removeMentionsFromValue(value: unknown): unknown {
  if (typeof value === 'string') return removeSourceMentions(value);
  if (Array.isArray(value)) return value.map(removeMentionsFromValue);
  if (value && typeof value === 'object') {
    const cleaned: Record<string, unknown> = {};
    for (const [field, fieldValue] of Object.entries(value)) {
      cleaned[field] = removeMentionsFromValue(fieldValue);
    }
    return cleaned;
  }
  return value;
}

// Cleans the source names out of every response before the demo can show them.
// /cache/read hands us raw JSON text, so parse it and clean the values one by one
// rather than doing a find-and-replace over the whole blob.
function removeMentionsFromBundle(incoming: SnapshotBundle): SnapshotBundle {
  const entries: Record<string, SnapshotEntry> = {};

  for (const [key, entry] of Object.entries(incoming.entries ?? {})) {
    if (typeof entry.data === 'string') {
      try {
        const cleaned = removeMentionsFromValue(JSON.parse(entry.data));
        entries[key] = { ...entry, data: JSON.stringify(cleaned) };
      } catch {
        entries[key] = { ...entry, data: removeSourceMentions(entry.data) }; // not JSON, plain text
      }
    } else {
      entries[key] = { ...entry, data: removeMentionsFromValue(entry.data) };
    }
  }

  return { ...incoming, entries };
}

export function loadBundle(next: SnapshotBundle): void {
  bundle = removeMentionsFromBundle(next);
}

export function isBundleLoaded(): boolean {
  return bundle !== null;
}

export function getMisses(): ReadonlyArray<SnapshotMiss> {
  return misses;
}

/**
 * Reduce a URL to a host-agnostic path so a bundle captured against dev-api
 * replays under any host. Also returns params embedded in the URL itself.
 */
export function normalizePath(
  url: string,
  baseURL?: string,
): { path: string; urlParams: Record<string, string> } {
  let raw = url || '';
  if (baseURL && raw.startsWith(baseURL)) raw = raw.slice(baseURL.length);

  let path = raw;
  let search = '';
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      path = u.pathname;
      search = u.search;
    } catch {
      path = raw;
    }
  } else {
    const q = raw.indexOf('?');
    if (q >= 0) {
      search = raw.slice(q);
      path = raw.slice(0, q);
    }
  }

  const urlParams: Record<string, string> = {};
  if (search) {
    new URLSearchParams(search).forEach((v, k) => {
      urlParams[k] = v;
    });
  }

  if (!path.startsWith('/')) path = `/${path}`;
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);

  return { path, urlParams };
}

function ignoredFor(path: string): string[] {
  const configured = bundle?.ignore_params ?? {};
  const extra = Object.keys(configured)
    .filter((prefix) => path.startsWith(prefix))
    .flatMap((prefix) => configured[prefix]);
  return [...GLOBAL_IGNORE_PARAMS, ...extra];
}

/** Stable key: METHOD path?sorted&params. Param order must never change the key. */
export function makeKey(
  method: string,
  path: string,
  params?: Record<string, unknown> | null,
): string {
  const ignore = ignoredFor(path);
  const entries = Object.entries(params ?? {})
    .filter(([k, v]) => !ignore.includes(k) && v !== undefined && v !== null)
    .map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : String(v)] as [string, string])
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));

  const query = entries.map(([k, v]) => `${k}=${v}`).join('&');
  return `${method.toUpperCase()} ${path}${query ? `?${query}` : ''}`;
}

/** Cache files that hold an object rather than an array. Everything else is a list. */
const OBJECT_SHAPED_CACHE_FILES = ['news_agg.json'];

/**
 * Flags the demo runs with. The app asks for these while it boots, before a capture
 * can be started, so they never make it into a bundle. Only enabled flags are ever
 * returned, so listing a key here turns it on.
 */
const DEMO_FEATURE_FLAGS = [{ feature_key: 'left_nav' }];

/**
 * Fail-closed empty value. A miss must never reach the network, but it also must not
 * crash a component expecting an array, so guess from the route.
 */
export function emptyShapeFor(path: string, params?: Record<string, unknown> | null): unknown {
  if (path.endsWith('/feature-flags')) return DEMO_FEATURE_FLAGS;

  // /cache/read serves whatever the file holds, and nearly every file is an array.
  if (path.endsWith('/cache/read')) {
    const filename = String(params?.filename ?? '');
    return OBJECT_SHAPED_CACHE_FILES.includes(filename) ? {} : [];
  }

  const last = path.split('/').filter(Boolean).pop() ?? '';
  const listish =
    last.endsWith('s') || /list|all|history|items|rows|data|series|flags/i.test(last);
  return listish ? [] : {};
}

export interface Resolution {
  hit: boolean;
  status: number;
  data: unknown;
  key: string;
}

export function resolve(
  method: string,
  url: string,
  params?: Record<string, unknown> | null,
  baseURL?: string,
): Resolution {
  const { path, urlParams } = normalizePath(url, baseURL);
  const merged = { ...urlParams, ...(params ?? {}) };
  const key = makeKey(method, path, merged);

  const entry = bundle?.entries?.[key];
  if (entry) {
    return {
      hit: true,
      status: entry.status ?? 200,
      data: trimToRecentMonths(merged, entry.data),
      key,
    };
  }

  // Fall back to a param-less fixture: still serves the page, never the network.
  const bareKey = makeKey(method, path, null);
  const bare = bundle?.entries?.[bareKey];
  if (bare) {
    return {
      hit: true,
      status: bare.status ?? 200,
      data: trimToRecentMonths(merged, bare.data),
      key: bareKey,
    };
  }

  misses.push({ method: method.toUpperCase(), path, params: merged, at: Date.now() });
  if (misses.length <= 100) {
    console.warn(`[demo] snapshot miss: ${key} — serving empty, no network`);
  }
  return { hit: false, status: 200, data: emptyShapeFor(path, merged), key };
}
